import { getCSRFToken } from './django'

const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:8000') + '/api'

const FIELD_LABELS = {
  name: 'Session name',
  business_unit: 'Business unit',
  template: 'Template',
  start_date: 'Session start date',
  end_date: 'Session end date',
  employee_stage_start_date: 'Staff review period start date',
  employee_stage_end_date: 'Staff review period end date',
  manager_stage_start_date: 'Manager review period start date',
  manager_stage_end_date: 'Manager review period end date',
  department_head_stage_start_date: 'Department Head review period start date',
  department_head_stage_end_date: 'Department Head review period end date',
  bu_head_stage_start_date: 'BU Head review period start date',
  bu_head_stage_end_date: 'BU Head review period end date',
  deadline_type: 'Deadline type',
  manager_review_flow: 'Delegation',
  status: 'Status'
}

function humanizeFieldName (field) {
  return FIELD_LABELS[field] || field.replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase())
}

function flattenErrorMessages (value) {
  if (Array.isArray(value)) return value.flatMap(flattenErrorMessages)
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([field, messages]) =>
      flattenErrorMessages(messages).map(message => `${humanizeFieldName(field)}: ${message}`)
    )
  }
  if (value === null || value === undefined || value === '') return []
  return [String(value)]
}

function getApiErrorMessage (body, status) {
  if (status === 401) return "You've been logged out. Please log in again."
  if (status === 403) return 'You are not authorized to perform this action.'
  if (body?.detail) return String(body.detail)
  const messages = flattenErrorMessages(body)
  return messages.length ? messages.join('\n') : `API error ${status}`
}

async function apiRequest (method, path, data) {
  const options = {
    method,
    headers: {
      Accept: 'application/json',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include'
  }
  if (data !== undefined) {
    options.headers['Content-Type'] = 'application/json'
    options.body = JSON.stringify(data)
  }
  const resp = await fetch(`${API_BASE}${path}`, options)
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    const err = new Error(getApiErrorMessage(body, resp.status))
    err.status = resp.status
    err.body = body
    throw err
  }
  if (resp.status === 204) return null
  return resp.json()
}

async function hijackRequest (path, data = {}) {
  const body = new URLSearchParams({ ...data, next: '/api/me/' })
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include',
    body
  })
  if (!response.ok) {
    throw new Error(response.status === 403
      ? 'You are not authorized to switch users.'
      : `Unable to switch users (${response.status}).`)
  }
  return response.json()
}

// Current user
export const getMe = () => apiRequest('GET', '/me/')
export const downloadPersonalData = async () => {
  const response = await fetch(`${API_BASE}/me/personal-data-export/`, {
    method: 'GET',
    headers: {
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include'
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    const error = new Error(getApiErrorMessage(body, response.status))
    error.status = response.status
    error.body = body
    throw error
  }
  const disposition = response.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="([^"]+)"/)
  return {
    blob: await response.blob(),
    filename: match?.[1] || 'personal-data-export.xlsx'
  }
}
export const getConsentStatus = () => apiRequest('GET', '/consent/status/')
export const respondToConsent = decision => apiRequest('POST', '/consent/respond/', { decision })
export async function getConsentDocument (document) {
  const response = await fetch(`${API_BASE}${document.url}`, {
    credentials: 'include'
  })
  if (!response.ok) throw new Error(`Unable to load the privacy notice (${response.status}).`)
  return response.text()
}
export const getNotifications = () => apiRequest('GET', '/notifications/')
export const markNotificationsSeen = (ids) => apiRequest('POST', '/notifications/mark-seen/', ids ? { ids } : {})
export const getManagerDigestExclusions = () => apiRequest('GET', '/manager-digest-exclusions/')
export const getManagerDigestExclusionCandidates = (query = '') => apiRequest('GET', `/manager-digest-exclusions/candidates/?q=${encodeURIComponent(query)}`)
export const createManagerDigestExclusion = managerId => apiRequest('POST', '/manager-digest-exclusions/', { manager_id: managerId })
export const deleteManagerDigestExclusion = managerId => apiRequest('DELETE', `/manager-digest-exclusions/${managerId}/`)

// Translation management
export const getTranslationLocales = () => apiRequest('GET', '/translations/locales/')
export const getTranslationDomains = () => apiRequest('GET', '/translations/domains/')
export const getTranslationBundle = (locale, domain) => {
  const params = new URLSearchParams()
  if (locale) params.set('locale', locale)
  if (domain) params.set('domain', domain)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/translations/bundle/${query}`)
}
export const getLanguageOptions = ({ sessionId, appraisalId } = {}) => {
  const params = new URLSearchParams()
  if (sessionId) params.set('session_id', sessionId)
  if (appraisalId) params.set('appraisal_id', appraisalId)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/language-options/${query}`)
}
export const getTranslations = ({ locale, domain, q, missingOnly, keyPrefix, excludeKeyPrefix, page, pageSize } = {}) => {
  const params = new URLSearchParams()
  if (locale) params.set('locale', locale)
  if (domain) params.set('domain', domain)
  if (q) params.set('q', q)
  if (keyPrefix) params.set('key_prefix', keyPrefix)
  if (excludeKeyPrefix) params.set('exclude_key_prefix', excludeKeyPrefix)
  if (missingOnly) params.set('missing_only', '1')
  if (page != null) params.set('page', page)
  if (pageSize != null) params.set('page_size', pageSize)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/translations/${query}`)
}
export const createTranslation = (data) => apiRequest('POST', '/translations/', data)
export const updateTranslation = (id, data) => apiRequest('PATCH', `/translations/${id}/`, data)
export const deleteTranslation = (id) => apiRequest('DELETE', `/translations/${id}/`)
export const syncAppraisalTemplateTranslations = (id) => apiRequest('POST', `/appraisal-templates/${id}/translation-sync/`)

// Departments
export const getDepartments = ({ region, organization, businessUnit } = {}) => {
  const params = new URLSearchParams()
  if (region != null) params.set('region', region)
  if (organization != null) params.set('organization', organization)
  if (businessUnit != null) params.set('business_unit', businessUnit)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/departments/${query}`)
}
export const getDepartment = (id) => apiRequest('GET', `/departments/${id}/`)
export const createDepartment = (data) => apiRequest('POST', '/departments/', data)
export const updateDepartment = (id, data) => apiRequest('PUT', `/departments/${id}/`, data)
export const deleteDepartment = (id) => apiRequest('DELETE', `/departments/${id}/`)
export const getSessionDepartments = (sessionId) => apiRequest('GET', `/sessions/${sessionId}/departments/`)
export const createSessionDepartment = (sessionId, data) => apiRequest('POST', `/sessions/${sessionId}/departments/`, data)
export const updateSessionDepartment = (sessionId, id, data) => apiRequest('PUT', `/sessions/${sessionId}/departments/${id}/`, data)
export const deleteSessionDepartment = (sessionId, id) => apiRequest('DELETE', `/sessions/${sessionId}/departments/${id}/`)
export const getSessionReviewPeriods = (sessionId) => apiRequest('GET', `/sessions/${sessionId}/review-periods/`)
export const updateSessionReviewPeriod = (sessionId, id, data) => apiRequest('PATCH', `/sessions/${sessionId}/review-periods/${id}/`, data)
export const getSessionEmailTemplates = (sessionId) => apiRequest('GET', `/sessions/${sessionId}/email-templates/`)
export const updateSessionEmailTemplate = (sessionId, id, data) => apiRequest('PATCH', `/sessions/${sessionId}/email-templates/${id}/`, data)
export const uploadSessionEmailTemplateImage = async (sessionId, file) => {
  const formData = new FormData()
  formData.append('image', file)
  const resp = await fetch(`${API_BASE}/sessions/${sessionId}/email-template-images/`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include',
    body: formData
  })
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    throw new Error(getApiErrorMessage(body, resp.status))
  }
  return resp.json()
}

// Business units and configurable appraisal questions
export const getRegionOptions = () => apiRequest('GET', '/regions/options/')
export const getOrganizationOptions = () => apiRequest('GET', '/organizations/options/')
export const getBusinessUnitOptions = ({ region, organization } = {}) => {
  const params = new URLSearchParams()
  if (region != null) params.set('region', region)
  if (organization != null) params.set('organization', organization)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/business-units/options/${query}`)
}
export const getTemplateOptions = () => apiRequest('GET', '/appraisal-templates/options/')
export const getTranslationReviewTemplates = () => apiRequest('GET', '/translation-review/templates/')
export const getBusinessUnits = () => apiRequest('GET', '/business-units/')
export const getBusinessUnit = (id) => apiRequest('GET', `/business-units/${id}/`)
export const createBusinessUnit = (data) => apiRequest('POST', '/business-units/', data)
export const updateBusinessUnit = (id, data) => apiRequest('PATCH', `/business-units/${id}/`, data)
export const deleteBusinessUnit = (id) => apiRequest('DELETE', `/business-units/${id}/`)
export const getAppraisalTemplates = () => apiRequest('GET', '/appraisal-templates/')
export const getAppraisalTemplate = (id) => apiRequest('GET', `/appraisal-templates/${id}/`)
export const createAppraisalTemplate = (data) => apiRequest('POST', '/appraisal-templates/', data)
export const updateAppraisalTemplate = (id, data) => apiRequest('PATCH', `/appraisal-templates/${id}/`, data)
export const deleteAppraisalTemplate = (id) => apiRequest('DELETE', `/appraisal-templates/${id}/`)
export const uploadAppraisalTemplateLogo = async (id, file) => {
  const formData = new FormData()
  formData.append('logo', file)
  const resp = await fetch(`${API_BASE}/appraisal-templates/${id}/logo/`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include',
    body: formData
  })
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    const err = new Error(getApiErrorMessage(body, resp.status))
    err.status = resp.status
    err.body = body
    throw err
  }
  return resp.json()
}
export const createQuestionCategory = (templateId, data) => apiRequest('POST', `/appraisal-templates/${templateId}/question-categories/`, data)
export const updateQuestionCategory = (id, data) => apiRequest('PATCH', `/question-categories/${id}/`, data)
export const deleteQuestionCategory = (id) => apiRequest('DELETE', `/question-categories/${id}/`)
export const createQuestion = (categoryId, data) => apiRequest('POST', `/question-categories/${categoryId}/questions/`, data)
export const updateQuestion = (id, data) => apiRequest('PATCH', `/questions/${id}/`, data)
export const deleteQuestion = (id) => apiRequest('DELETE', `/questions/${id}/`)
export const getFormStructure = (businessUnitCode, locale, { translationReview = false } = {}) => {
  const params = new URLSearchParams()
  if (locale) params.set('locale', locale)
  if (translationReview) params.set('translation_review', '1')
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/form-structure/${businessUnitCode}/${query}`)
}
export const searchStaff = ({ q, role, sessionId } = {}) => {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (role) params.set('role', role)
  if (sessionId != null) params.set('session_id', sessionId)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/staff/search/${query}`)
}

// Sessions
export const getGlobalHROverview = ({ region, status, q, page, pageSize } = {}) => {
  const params = new URLSearchParams()
  if (region) params.set('region', region)
  if (status) params.set('status', status)
  if (q) params.set('q', q)
  if (page != null) params.set('page', page)
  if (pageSize != null) params.set('page_size', pageSize)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/global-hr/overview/${query}`)
}
export const getSessions = () => apiRequest('GET', '/sessions/')
export const getSession = (id) => apiRequest('GET', `/sessions/${id}/`)
export const getSessionHRUsers = (id) => apiRequest('GET', `/sessions/${id}/hr-users/`)
export const createSessionHRUser = (id, data) => apiRequest('POST', `/sessions/${id}/hr-users/`, data)
export const updateSessionHRUser = (id, roleId, data) => apiRequest('PATCH', `/sessions/${id}/hr-users/${roleId}/`, data)
export const getSessionReadOnlyReport = (id) => apiRequest('GET', `/sessions/${id}/read-only-report/`)
export const uploadSessionReadOnlyReport = async (id, file) => {
  const form = new FormData()
  form.append('file', file)
  const response = await fetch(`${API_BASE}/sessions/${id}/read-only-report/`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include',
    body: form
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(getApiErrorMessage(body, response.status))
  }
  return response.json()
}
export const createSession = (data) => apiRequest('POST', '/sessions/', data)
export const updateSession = (id, data) => apiRequest('PATCH', `/sessions/${id}/`, data)
export const patchSession = (id, data) => apiRequest('PATCH', `/sessions/${id}/`, data)
export const deleteSession = (id) => apiRequest('DELETE', `/sessions/${id}/`)
export const triggerSessionEmails = (id) => apiRequest('POST', `/sessions/${id}/trigger-emails/`)
export const sendSessionProcessOverviewEmails = (id) => apiRequest('POST', `/sessions/${id}/send-process-overview-emails/`)
export const sendSessionTestEmails = (id, email) => apiRequest('POST', `/sessions/${id}/send-test-emails/`, { email })
export const sendAllSessionTestEmailsToCurrentUser = (id) => apiRequest('POST', `/sessions/${id}/send-test-emails/`, { all_templates: true })
export const sendSessionTemplateTestEmailToCurrentUser = (id, template) => apiRequest('POST', `/sessions/${id}/send-test-emails/`, {
  template_id: template.id,
  subject_template: template.subject_template,
  body_template: template.body_template
})
export const sendAppraisalEmployeeInvitation = (id) => apiRequest('POST', `/appraisals/${id}/send-employee-invitation/`)
export const addSessionEmployee = (sessionId, payload) => apiRequest('POST', `/sessions/${sessionId}/add-employee/`, payload)
export const removeSessionEmployee = (sessionId, employeeId) => apiRequest('POST', `/sessions/${sessionId}/remove-employee/`, { employee_id: employeeId })
const safeDownloadName = (value, fallback) => {
  const cleaned = String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return cleaned || fallback
}

export const exportSessionExcel = async (sessionId, sessionName = '') => {
  const resp = await fetch(`${API_BASE}/sessions/${sessionId}/export-excel/`, {
    method: 'GET',
    headers: {
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include'
  })

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    const err = new Error(getApiErrorMessage(body, resp.status))
    err.status = resp.status
    err.body = body
    throw err
  }

  const disposition = resp.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const fallbackName = `${safeDownloadName(sessionName, `session-${sessionId}`)}-export.xlsx`
  const filename = match?.[1] || fallbackName
  const blob = await resp.blob()
  return { blob, filename }
}

export const exportSessionBulkPdf = async (sessionId, appraisalIds) => {
  const resp = await fetch(`${API_BASE}/sessions/${sessionId}/appraisals/bulk-pdf/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCSRFToken() },
    credentials: 'include',
    body: JSON.stringify({ appraisal_ids: appraisalIds })
  })
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    throw new Error(getApiErrorMessage(body, resp.status))
  }
  const match = (resp.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)
  return { blob: await resp.blob(), filename: match?.[1] || `session-${sessionId}-appraisals.zip` }
}

export const bulkAutoApproveSessionAppraisals = (sessionId, appraisalIds) => apiRequest(
  'POST',
  `/sessions/${sessionId}/appraisals/bulk-auto-approve/`,
  { appraisal_ids: appraisalIds }
)

export const downloadSessionImportExample = async (sessionId, sessionName = '') => {
  const resp = await fetch(`${API_BASE}/sessions/${sessionId}/imports/employees-excel/example/`, {
    method: 'GET',
    headers: {
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include'
  })

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    const err = new Error(getApiErrorMessage(body, resp.status))
    err.status = resp.status
    err.body = body
    throw err
  }

  const disposition = resp.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const fallbackName = `${safeDownloadName(sessionName, `session-${sessionId}`)}-import-example.xlsx`
  const filename = match?.[1] || fallbackName
  const blob = await resp.blob()
  return { blob, filename }
}

// Appraisals
export const getAppraisals = (params) => {
  const queryParams = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') queryParams.set(key, value)
  })
  const queryString = queryParams.toString()
  const query = queryString ? `?${queryString}` : ''
  return apiRequest('GET', `/appraisals/${query}`)
}
export const getAppraisal = (id, params = {}) => {
  const queryParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') queryParams.set(key, value)
  })
  const queryString = queryParams.toString()
  const query = queryString ? `?${queryString}` : ''
  return apiRequest('GET', `/appraisals/${id}/${query}`)
}
export const createAppraisal = (data) => apiRequest('POST', '/appraisals/', data)
export const updateAppraisal = (id, data) => apiRequest('PUT', `/appraisals/${id}/`, data)
export const updateAppraisalStaff = (id, data) => apiRequest('PATCH', `/appraisals/${id}/staff/`, data)
export const getAppraisalPdfUrl = (id) => `${API_BASE}/appraisals/${id}/export-pdf/`
export const exportAppraisalPdf = async (id) => {
  const resp = await fetch(`${API_BASE}/appraisals/${id}/export-pdf/`, {
    method: 'GET',
    headers: {
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include'
  })

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    const err = new Error(getApiErrorMessage(body, resp.status))
    err.status = resp.status
    err.body = body
    throw err
  }

  const disposition = resp.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename = match?.[1] || `appraisal-${id}.pdf`
  const blob = await resp.blob()
  return { blob, filename }
}
export const deleteAppraisal = (id) => apiRequest('DELETE', `/appraisals/${id}/`)
export const getBulkDelegateCandidates = (delegationMode) => apiRequest('GET', `/appraisals/delegate-candidates/?delegation_mode=${encodeURIComponent(delegationMode || '')}`)
export const createDelegateCandidate = (appraisalIds, candidate, delegationMode) => apiRequest('POST', '/appraisals/delegate-candidates/', {
  appraisal_ids: appraisalIds,
  delegation_mode: delegationMode,
  ...candidate
})
export const bulkDelegateAppraisals = (appraisalIds, delegateToUserIds, delegationMode, requestHrAssignment = false) => {
  const reviewerIds = (Array.isArray(delegateToUserIds) ? delegateToUserIds : [delegateToUserIds]).filter(Boolean)
  const delegatePayload = reviewerIds.length === 0
    ? {}
    : delegationMode === 'functional_review'
      ? { delegate_to_user_ids: reviewerIds }
      : { delegate_to_user_id: reviewerIds[0] }
  return apiRequest('POST', '/appraisals/delegate-bulk/', {
    appraisal_ids: appraisalIds,
    delegation_mode: delegationMode,
    request_hr_assignment: requestHrAssignment,
    ...delegatePayload
  })
}
export const getDelegationRequests = (status = 'pending', sessionId = null) => {
  const params = new URLSearchParams({ status })
  if (sessionId) params.set('session', sessionId)
  return apiRequest('GET', `/delegation-requests/?${params.toString()}`)
}
export const getDelegationRequestCount = (status = 'pending', sessionId = null) => {
  const params = new URLSearchParams({ status, count_only: '1' })
  if (sessionId) params.set('session', sessionId)
  return apiRequest('GET', `/delegation-requests/?${params.toString()}`)
}
export const decideDelegationRequest = (id, action, reviewComments = '', delegateToUserId = null) => apiRequest('POST', `/delegation-requests/${id}/decision/`, {
  action,
  review_comments: reviewComments,
  ...(delegateToUserId ? { delegate_to_user_id: delegateToUserId } : {})
})
export const getAppraisalDelegateCandidates = (id, delegationMode) => apiRequest('GET', `/appraisals/${id}/delegate-candidates/?delegation_mode=${encodeURIComponent(delegationMode || '')}`)
export const delegateAppraisal = (id, delegateToUserIds, delegationMode) => {
  const reviewerIds = Array.isArray(delegateToUserIds) ? delegateToUserIds : [delegateToUserIds]
  const delegatePayload = delegationMode === 'functional_review'
    ? { delegate_to_user_ids: reviewerIds }
    : { delegate_to_user_id: reviewerIds[0] }
  return apiRequest('POST', `/appraisals/${id}/delegate/`, { delegation_mode: delegationMode, ...delegatePayload })
}
export const functionalReviewDraft = (id, data) => apiRequest('POST', `/appraisals/${id}/functional-review-draft/`, data)
export const functionalReviewSubmit = (id, data) => apiRequest('POST', `/appraisals/${id}/functional-review-submit/`, data)
export const revokeFunctionalReview = (appraisalId, reviewId) => apiRequest('DELETE', `/appraisals/${appraisalId}/functional-reviews/${reviewId}/revoke/`)

// Rating distribution
export const getRatingDistribution = (sessionId, scope) => {
  const params = new URLSearchParams()
  if (sessionId) params.set('session', String(sessionId))
  if (scope) params.set('scope', scope)
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/rating-distribution/${query}`)
}

// Submission endpoints
export const employeeSubmit = (id, data) => apiRequest('POST', `/appraisals/${id}/employee-submit/`, data)
export const employeeDraft = (id, data) => apiRequest('POST', `/appraisals/${id}/employee-draft/`, data)
export const managerSubmit = (id, data) => apiRequest('POST', `/appraisals/${id}/manager-submit/`, data)
export const managerDraft = (id, data) => apiRequest('POST', `/appraisals/${id}/manager-draft/`, data)
export const headSignoff = (id, data) => apiRequest('POST', `/appraisals/${id}/head-signoff/`, data)
export const buHeadSignoff = (id, data) => apiRequest('POST', `/appraisals/${id}/bu-head-signoff/`, data)
export const additionalApproverSignoff = (id, level, data) => apiRequest('POST', `/appraisals/${id}/approver-${level}-signoff/`, data)
export const bulkBuHeadSignoff = (appraisalIds, comments = '') => apiRequest('POST', '/appraisals/bu-head-signoff-bulk/', { appraisal_ids: appraisalIds, bu_head_comments: comments })
export const bulkApproverSignoff = (appraisalIds, comments = '') => apiRequest('POST', '/appraisals/signoff-bulk/', { appraisal_ids: appraisalIds, comments })
export const returnAppraisalToPreviousStage = (id, comments, review = {}) => apiRequest('POST', `/appraisals/${id}/return-to-previous-stage/`, { comments, ...review })
export const returnAppraisalToEmployee = (id, comments, review = {}) => apiRequest('POST', `/appraisals/${id}/return-to-employee/`, { comments, ...review })

// Developer/Testing Tools (Staff Only)
export const getDevUsers = ({ sessionId, appraisalId, hrOnly } = {}) => {
  const params = new URLSearchParams()
  if (sessionId) params.set('session_id', sessionId)
  if (appraisalId) params.set('appraisal_id', appraisalId)
  if (hrOnly) params.set('hr_only', '1')
  const query = params.toString() ? `?${params.toString()}` : ''
  return apiRequest('GET', `/dev/users/${query}`)
}
export const hijackUser = (userId) => hijackRequest('/hijack/acquire/', { user_pk: userId })
export const releaseHijackedUser = () => hijackRequest('/hijack/release/')

// Batched employee Excel import (upload once, then process rows in small batches)
export const createSessionEmployeesExcelImportJob = (sessionId, file, { onProgress } = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE}/sessions/${sessionId}/imports/employees-excel/`)
    xhr.withCredentials = true
    xhr.setRequestHeader('X-CSRFToken', getCSRFToken())

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.round((evt.loaded / evt.total) * 100))
      }
    }

    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || '{}')
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data)
        } else {
          const err = new Error(getApiErrorMessage(data, xhr.status))
          err.status = xhr.status
          err.body = data
          reject(err)
        }
      } catch (e) {
        reject(e)
      }
    }
    xhr.onerror = () => reject(new Error('Network error'))

    const form = new FormData()
    form.append('file', file)
    xhr.send(form)
  })
}

export const processSessionEmployeesExcelImportJobBatch = (sessionId, jobId, { batchSize = 10 } = {}) => {
  return apiRequest('POST', `/sessions/${sessionId}/imports/employees-excel/${jobId}/process/`, {
    batch_size: batchSize
  })
}

export const getSessionEmployeesExcelImportJobStatus = (sessionId, jobId) => {
  return apiRequest('GET', `/sessions/${sessionId}/imports/employees-excel/${jobId}/`)
}

export const importSessionEmployeesExcel = (sessionId, file, { batchSize = 10, onProgress } = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE}/sessions/${sessionId}/import-employees-excel/`)
    xhr.withCredentials = true
    xhr.setRequestHeader('X-CSRFToken', getCSRFToken())

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.round((evt.loaded / evt.total) * 100))
      }
    }

    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || '{}')
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data)
        } else {
          const err = new Error(getApiErrorMessage(data, xhr.status))
          err.status = xhr.status
          err.body = data
          reject(err)
        }
      } catch (e) {
        reject(e)
      }
    }
    xhr.onerror = () => reject(new Error('Network error'))

    const form = new FormData()
    form.append('file', file)
    form.append('batch_size', String(batchSize))
    xhr.send(form)
  })
}
